import React, { useMemo, useState } from 'react'
import Button from '../../../ui/Button/Button'
import { assertProductActivationReadiness } from './productTaxonomyFields'
import {
  buildCatalogTaxonomyNodePathLabel,
  buildCatalogTaxonomyNodePathSegments,
} from './formatProductTaxonomySummary'
import CatalogTaxonomyNodePicker from './CatalogTaxonomyNodePicker'
import {
  applyAddExtraCategory,
  applyRemoveCategoryAssignment,
  applySetMainCategory,
  splitMainAndExtraAssignments,
} from './productTaxonomyUx'
import './ProductTaxonomyAssignmentPanel.sass'

const PICKER_MODE = {
  main: 'main',
  extra: 'extra',
  recoverMain: 'recoverMain',
}

function groupExtrasByParent(extraIds, nodesById) {
  const groups = new Map()
  extraIds.forEach((id) => {
    const node = nodesById[id]
    const parentId = node?.parentId || null
    const groupKey = parentId || '__root__'
    if (!groups.has(groupKey)) {
      groups.set(groupKey, {
        parentSegments: parentId ? buildCatalogTaxonomyNodePathSegments(parentId, nodesById) : [],
        leaves: [],
      })
    }
    groups.get(groupKey).leaves.push({
      id,
      name: node?.name || id,
    })
  })
  return Array.from(groups.values())
}

function ProductTaxonomyAssignmentPanel({
  assignmentIds = [],
  canonicalId = null,
  nodesById = {},
  nodes = [],
  productSnapshot = {},
  onChange,
}) {
  const [pickerMode, setPickerMode] = useState(null)

  const split = useMemo(
    () =>
      splitMainAndExtraAssignments({
        catalogTaxonomyAssignmentIds: assignmentIds,
        canonicalCatalogTaxonomyNodeId: canonicalId,
      }),
    [assignmentIds, canonicalId]
  )

  const extrasChipGroups = useMemo(
    () => groupExtrasByParent(split.extraIds, nodesById),
    [split.extraIds, nodesById]
  )

  const readiness = useMemo(() => {
    return assertProductActivationReadiness(
      {
        ...productSnapshot,
        catalogTaxonomyAssignmentIds: assignmentIds,
        canonicalCatalogTaxonomyNodeId: canonicalId,
      },
      nodesById
    )
  }, [productSnapshot, assignmentIds, canonicalId, nodesById])

  const emit = (next) => {
    onChange({
      catalogTaxonomyAssignmentIds: next.catalogTaxonomyAssignmentIds,
      canonicalCatalogTaxonomyNodeId: next.canonicalCatalogTaxonomyNodeId,
    })
  }

  const closePicker = () => setPickerMode(null)

  const handleRemoveExtra = (nodeId) => {
    const path = buildCatalogTaxonomyNodePathLabel(nodeId, nodesById) || nodeId
    const confirmed = window.confirm(
      `Прибрати додаткову категорію?\n\n«${path}»\n\nЗміни застосуються після збереження товару.`
    )
    if (!confirmed) return

    const result = applyRemoveCategoryAssignment({
      assignmentIds,
      previousCanonical: canonicalId,
      removeId: nodeId,
    })
    if (!result.ok) {
      window.alert(result.message)
      return
    }
    emit(result)
  }

  const handlePick = (node, guard) => {
    if (pickerMode === PICKER_MODE.extra && assignmentIds.includes(node.id)) {
      if (node.id === canonicalId) return
      handleRemoveExtra(node.id)
      return
    }

    if (!guard.allowed) {
      window.alert(guard.message || 'Не можна призначити цей вузол.')
      return
    }
    if (guard.warning) window.alert(guard.warning)

    if (pickerMode === PICKER_MODE.main || pickerMode === PICKER_MODE.recoverMain) {
      if (canonicalId && canonicalId !== node.id && split.hasMain) {
        const currentPath = buildCatalogTaxonomyNodePathLabel(canonicalId, nodesById)
        const nextPath = buildCatalogTaxonomyNodePathLabel(node.id, nodesById)
        const confirmed = window.confirm(
          `Змінити основну категорію?\n\nПоточна:\n${currentPath || canonicalId}\n\nНова:\n${nextPath || node.id}`
        )
        if (!confirmed) return
      }
      const result = applySetMainCategory({
        assignmentIds,
        previousCanonical: canonicalId,
        mainId: node.id,
      })
      if (!result.ok) {
        window.alert(result.message)
        return
      }
      emit(result)
      closePicker()
      return
    }

    if (pickerMode === PICKER_MODE.extra) {
      const result = applyAddExtraCategory({
        assignmentIds,
        previousCanonical: canonicalId,
        extraId: node.id,
      })
      if (!result.ok) {
        window.alert(result.message)
        return
      }
      emit(result)
    }
  }

  const mainPath = split.mainId
    ? buildCatalogTaxonomyNodePathLabel(split.mainId, nodesById)
    : ''
  const extrasEnabled = split.hasMain
  const mainPickerOpen =
    pickerMode === PICKER_MODE.main || pickerMode === PICKER_MODE.recoverMain
  const extrasPickerOpen = pickerMode === PICKER_MODE.extra

  return (
    <div className="ProductTaxonomyAssignmentPanel">
      <p className="Title">Категорії каталогу</p>

      <section className="ProductTaxonomyAssignmentPanel-Block">
        <div className="ProductTaxonomyAssignmentPanel-BlockHead">
          <h3 className="ProductTaxonomyAssignmentPanel-BlockTitle">Основна категорія</h3>
          {split.hasMain && !split.mainInvalid ? (
            <Button
              title={mainPickerOpen ? 'Сховати' : 'Змінити'}
              theme="solid"
              fill="normal"
              size={28}
              onClick={() =>
                setPickerMode((prev) => (prev === PICKER_MODE.main ? null : PICKER_MODE.main))
              }
            />
          ) : !split.mainInvalid && !split.needsMainRecovery ? (
            <Button
              title={mainPickerOpen ? 'Сховати' : 'Обрати'}
              theme="solid"
              fill="normal"
              size={28}
              onClick={() =>
                setPickerMode((prev) => (prev === PICKER_MODE.main ? null : PICKER_MODE.main))
              }
            />
          ) : null}
        </div>

        {split.mainInvalid ? (
          <div className="ProductTaxonomyAssignmentPanel-Recovery">
            <p>Основна категорія налаштована некоректно. Оберіть її повторно.</p>
            <Button
              title="Обрати основну категорію"
              theme="solid"
              fill="accent"
              size={32}
              onClick={() => setPickerMode(PICKER_MODE.main)}
            />
          </div>
        ) : split.needsMainRecovery ? (
          <div className="ProductTaxonomyAssignmentPanel-Recovery">
            <p>Оберіть основну категорію серед уже доданих</p>
            <div className="ProductTaxonomyAssignmentPanel-RecoveryActions">
              <select
                className="ProductTaxonomyAssignmentPanel-Select"
                value=""
                onChange={(e) => {
                  const mainId = e.target.value
                  if (!mainId) return
                  const result = applySetMainCategory({
                    assignmentIds,
                    previousCanonical: canonicalId,
                    mainId,
                  })
                  if (result.ok) emit(result)
                }}
              >
                <option value="">— оберіть серед доданих —</option>
                {assignmentIds.map((id) => (
                  <option key={id} value={id}>
                    {buildCatalogTaxonomyNodePathLabel(id, nodesById) || id}
                  </option>
                ))}
              </select>
              <Button
                title="Обрати нову основну"
                theme="solid"
                fill="normal"
                size={32}
                onClick={() => setPickerMode(PICKER_MODE.recoverMain)}
              />
            </div>
          </div>
        ) : split.mainId ? (
          <div className="ProductTaxonomyAssignmentPanel-MainChip">
            <span>{mainPath || split.mainId}</span>
          </div>
        ) : (
          <div className="ProductTaxonomyAssignmentPanel-EmptyRow">
            <span>Не вибрано</span>
          </div>
        )}

        {mainPickerOpen ? (
          <div className="ProductTaxonomyAssignmentPanel-Picker">
            <CatalogTaxonomyNodePicker
              nodes={nodes}
              nodesById={nodesById}
              assignedIds={split.mainId ? [split.mainId] : []}
              mode="select"
              hint=""
              focusNodeId={split.mainId}
              onPick={handlePick}
            />
            <Button
              className="ProductTaxonomyAssignmentPanel-Done"
              title="Готово"
              theme="solid"
              fill="accent"
              size={32}
              onClick={closePicker}
            />
          </div>
        ) : null}
      </section>

      <section
        className={[
          'ProductTaxonomyAssignmentPanel-Block',
          !extrasEnabled ? 'ProductTaxonomyAssignmentPanel-Block_disabled' : '',
        ]
          .filter(Boolean)
          .join(' ')}
      >
        <div className="ProductTaxonomyAssignmentPanel-BlockHead">
          <h3 className="ProductTaxonomyAssignmentPanel-BlockTitle">Додаткові категорії</h3>
          {extrasEnabled ? (
            <Button
              title={extrasPickerOpen ? 'Сховати' : 'Змінити'}
              theme="solid"
              fill="normal"
              size={28}
              onClick={() =>
                setPickerMode((prev) => (prev === PICKER_MODE.extra ? null : PICKER_MODE.extra))
              }
            />
          ) : null}
        </div>

        {!extrasEnabled ? (
          <p className="ProductTaxonomyAssignmentPanel-Empty">Спочатку оберіть основну категорію</p>
        ) : extrasChipGroups.length === 0 ? (
          <p className="ProductTaxonomyAssignmentPanel-Empty">Не обрано</p>
        ) : (
          <ul className="ProductTaxonomyAssignmentPanel-ChipPaths">
            {extrasChipGroups.map((group) => {
              const groupKey =
                group.parentSegments.map((s) => s.id).join('>') ||
                group.leaves.map((l) => l.id).join(',')
              return (
                <li key={groupKey} className="ProductTaxonomyAssignmentPanel-ChipPath">
                  {group.parentSegments.map((segment, index) => (
                    <React.Fragment key={`${groupKey}-p-${segment.id}`}>
                      {index > 0 ? (
                        <span className="ProductTaxonomyAssignmentPanel-ChipSep">→</span>
                      ) : null}
                      <span className="ProductTaxonomyAssignmentPanel-Chip ProductTaxonomyAssignmentPanel-Chip_ancestor">
                        {segment.name}
                      </span>
                    </React.Fragment>
                  ))}
                  {group.parentSegments.length ? (
                    <span className="ProductTaxonomyAssignmentPanel-ChipSep">→</span>
                  ) : null}
                  <span className="ProductTaxonomyAssignmentPanel-ChipLeaves">
                    {group.leaves.map((leaf) => (
                      <span
                        key={leaf.id}
                        className="ProductTaxonomyAssignmentPanel-Chip ProductTaxonomyAssignmentPanel-Chip_leaf"
                      >
                        <span>{leaf.name}</span>
                        <button
                          type="button"
                          title="Прибрати"
                          aria-label={`Прибрати ${leaf.name}`}
                          onClick={() => handleRemoveExtra(leaf.id)}
                        >
                          ×
                        </button>
                      </span>
                    ))}
                  </span>
                </li>
              )
            })}
          </ul>
        )}

        {extrasPickerOpen ? (
          <div className="ProductTaxonomyAssignmentPanel-Picker">
            <CatalogTaxonomyNodePicker
              nodes={nodes}
              nodesById={nodesById}
              assignedIds={assignmentIds}
              mode="select"
              hint=""
              focusNodeId={split.extraIds[0] || null}
              onPick={handlePick}
            />
            <Button
              className="ProductTaxonomyAssignmentPanel-Done"
              title="Готово"
              theme="solid"
              fill="accent"
              size={32}
              onClick={closePicker}
            />
          </div>
        ) : null}
      </section>

      {readiness.warnings.length > 0 ? (
        <div className="ProductTaxonomyAssignmentPanel-Messages ProductTaxonomyAssignmentPanel-Messages_warnings">
          {readiness.warnings.map((item) => (
            <p key={`${item.code}-${item.message}`}>{item.message}</p>
          ))}
        </div>
      ) : null}
    </div>
  )
}

export default ProductTaxonomyAssignmentPanel
